import { Locales } from '@griply/types';
import { DateFormatOptions } from 'shared/types/date-format-options';
import {
  CLEAR_DATE_KEYWORDS,
  DAY_NUMBER_ARTICLES,
} from 'shared/utils/nlp/date-processing';

import {
  getDayIndexMap,
  getMonthMap,
  getRruleKeywords,
} from './locale-vocabulary';

// "próximo viernes" skips a week even when Friday is tomorrow; "viernes que viene" does not.
enum NextDayWeek {
  Following = 'following',
  Soonest = 'soonest',
}

type NextDayForm = {
  // Group 1 is the day token, so prefix and postfix affixes share one shape.
  pattern: RegExp;
  week: NextDayWeek;
};

// Both Portuguese variants share one date grammar; only UI wording differs.
const portugueseNextDayForms: NextDayForm[] = [
  {
    pattern: /^(?:n?[ao]\s+)?pr[óo]xim[oa]\s+(\S+)$/,
    week: NextDayWeek.Following,
  },
  { pattern: /^(\S+)\s+que\s+vem$/, week: NextDayWeek.Soonest },
];

const NEXT_DAY_FORMS: Record<Locales, NextDayForm[]> = {
  [Locales.EnUS]: [{ pattern: /^next\s+(\S+)$/, week: NextDayWeek.Following }],
  [Locales.NlNL]: [
    { pattern: /^volgende\s+(\S+)$/, week: NextDayWeek.Following },
  ],
  [Locales.EsES]: [
    {
      pattern: /^(?:el\s+)?pr[óo]xim[oa]\s+(\S+)$/,
      week: NextDayWeek.Following,
    },
    { pattern: /^(?:el\s+)?(\S+)\s+que\s+viene$/, week: NextDayWeek.Soonest },
  ],
  [Locales.DeDE]: [
    { pattern: /^n(?:ä|ae)chsten\s+(\S+)$/, week: NextDayWeek.Following },
  ],
  [Locales.PlPL]: [
    {
      pattern: /^(?:w\s+)?przysz[łl][yąa]\s+(\S+)$/,
      week: NextDayWeek.Following,
    },
  ],
  [Locales.DaDK]: [
    { pattern: /^n(?:æ|ae)ste\s+(\S+)$/, week: NextDayWeek.Following },
  ],
  [Locales.SvSE]: [
    { pattern: /^n(?:ä|a)sta\s+(\S+)$/, week: NextDayWeek.Following },
  ],
  [Locales.NbNO]: [{ pattern: /^neste\s+(\S+)$/, week: NextDayWeek.Following }],
  [Locales.ItIT]: [
    {
      pattern: /^(?:il\s+)?pross(?:imo|ima)\s+(\S+)$/,
      week: NextDayWeek.Following,
    },
    { pattern: /^(\S+)\s+pross(?:imo|ima)$/, week: NextDayWeek.Following },
  ],
  [Locales.FrFR]: [
    {
      pattern: /^(?:le\s+)?prochain(?:e)?\s+(\S+)$/,
      week: NextDayWeek.Following,
    },
    { pattern: /^(\S+)\s+prochain(?:e)?$/, week: NextDayWeek.Following },
  ],
  [Locales.PtPT]: portugueseNextDayForms,
  [Locales.PtBR]: portugueseNextDayForms,
};

type RelativeDateKeywords = {
  today: string[];
  tomorrow: string[];
  day_after_tomorrow: string[];
  next_week: string[];
  this_weekend: string[];
  next_weekend: string[];
  next_year: string[];
  next_month: string[];
  end_of_month: string[];
};

const RELATIVE_DATE_KEYWORDS: Record<Locales, RelativeDateKeywords> = {
  [Locales.EnUS]: {
    today: ['today', 'tod'],
    tomorrow: ['tomorrow', 'tmw', 'tmr'],
    day_after_tomorrow: ['day after tomorrow'],
    next_week: ['next week'],
    this_weekend: ['this weekend'],
    next_weekend: ['next weekend'],
    next_year: ['next year'],
    next_month: ['next month'],
    end_of_month: ['end of month'],
  },
  [Locales.NlNL]: {
    today: ['vandaag'],
    tomorrow: ['morgen'],
    day_after_tomorrow: ['overmorgen'],
    next_week: ['volgende week'],
    this_weekend: ['dit weekend'],
    next_weekend: ['volgend weekend'],
    next_year: ['volgend jaar'],
    next_month: ['volgende maand'],
    end_of_month: ['eind van de maand'],
  },
  [Locales.EsES]: {
    today: ['hoy'],
    tomorrow: ['mañana'],
    day_after_tomorrow: ['pasado mañana', 'pasado manana'],
    next_week: [
      'la proxima semana',
      'la próxima semana',
      'proxima semana',
      'próxima semana',
    ],
    this_weekend: ['este fin de semana'],
    next_weekend: [
      'el proximo fin de semana',
      'el próximo fin de semana',
      'proximo fin de semana',
      'próximo fin de semana',
    ],
    next_year: [
      'el proximo año',
      'el próximo año',
      'proximo año',
      'próximo año',
    ],
    next_month: [
      'el proximo mes',
      'el próximo mes',
      'proximo mes',
      'próximo mes',
    ],
    end_of_month: ['fin de mes'],
  },
  [Locales.DeDE]: {
    today: ['heute'],
    tomorrow: ['morgen'],
    day_after_tomorrow: ['übermorgen', 'uebermorgen'],
    next_week: ['nächste woche', 'naechste woche'],
    this_weekend: ['dieses wochenende'],
    next_weekend: ['nächstes wochenende', 'naechstes wochenende'],
    next_year: ['nächstes jahr', 'naechstes jahr'],
    next_month: ['nächsten monat', 'naechsten monat'],
    end_of_month: ['ende des monats'],
  },
  [Locales.PlPL]: {
    today: ['dziś', 'dzis', 'dzisiaj'],
    tomorrow: ['jutro'],
    day_after_tomorrow: ['pojutrze'],
    next_week: [
      'w przyszłym tygodniu',
      'w przyszlym tygodniu',
      'przyszły tydzień',
      'przyszly tydzien',
    ],
    this_weekend: ['w ten weekend', 'ten weekend'],
    next_weekend: [
      'w przyszły weekend',
      'w przyszly weekend',
      'przyszły weekend',
      'przyszly weekend',
    ],
    next_year: [
      'w przyszłym roku',
      'w przyszlym roku',
      'przyszły rok',
      'przyszly rok',
    ],
    next_month: [
      'w przyszłym miesiącu',
      'w przyszlym miesiacu',
      'przyszły miesiąc',
      'przyszly miesiac',
    ],
    end_of_month: ['koniec miesiąca', 'koniec miesiaca'],
  },
  [Locales.DaDK]: {
    today: ['i dag', 'idag'],
    tomorrow: ['i morgen', 'imorgen'],
    day_after_tomorrow: ['i overmorgen', 'iovermorgen'],
    next_week: ['næste uge', 'naeste uge'],
    this_weekend: ['denne weekend'],
    next_weekend: ['næste weekend', 'naeste weekend'],
    next_year: ['næste år', 'naeste aar'],
    next_month: ['næste måned', 'naeste maaned'],
    end_of_month: ['slutningen af måneden', 'slutningen af maaneden'],
  },
  [Locales.SvSE]: {
    today: ['i dag', 'idag'],
    tomorrow: ['i morgon', 'imorgon'],
    day_after_tomorrow: ['i övermorgon', 'i overmorgon'],
    next_week: ['nästa vecka', 'nasta vecka'],
    this_weekend: ['denna helg'],
    next_weekend: ['nästa helg', 'nasta helg'],
    next_year: ['nästa år', 'nasta ar'],
    next_month: ['nästa månad', 'nasta manad'],
    end_of_month: ['slutet av månaden', 'slutet av manaden'],
  },
  [Locales.NbNO]: {
    today: ['i dag', 'idag'],
    tomorrow: ['i morgen', 'imorgen'],
    day_after_tomorrow: ['i overmorgen'],
    next_week: ['neste uke'],
    this_weekend: ['denne helg', 'denne helgen'],
    next_weekend: ['neste helg'],
    next_year: ['neste år', 'neste aar'],
    next_month: ['neste måned', 'neste maaned'],
    end_of_month: ['slutten av måneden', 'slutten av maaneden'],
  },
  [Locales.ItIT]: {
    today: ['oggi'],
    tomorrow: ['domani'],
    day_after_tomorrow: ['dopodomani'],
    next_week: ['la prossima settimana', 'prossima settimana'],
    this_weekend: ['questo weekend', 'questo fine settimana'],
    next_weekend: ['il prossimo weekend', 'prossimo weekend'],
    next_year: ['il prossimo anno', 'prossimo anno'],
    next_month: ['il prossimo mese', 'prossimo mese'],
    end_of_month: ['fine mese', 'fine del mese'],
  },
  [Locales.FrFR]: {
    today: ["aujourd'hui", 'aujourd hui'],
    tomorrow: ['demain'],
    day_after_tomorrow: ['après-demain', 'apres-demain'],
    next_week: ['la semaine prochaine', 'semaine prochaine'],
    this_weekend: ['ce week-end', 'ce weekend'],
    next_weekend: ['le week-end prochain', 'week-end prochain'],
    next_year: ["l'année prochaine", 'année prochaine', 'annee prochaine'],
    next_month: ['le mois prochain', 'mois prochain'],
    end_of_month: ['fin du mois', 'fin de mois'],
  },
  [Locales.PtPT]: {
    today: ['hoje'],
    tomorrow: ['amanhã', 'amanha'],
    day_after_tomorrow: ['depois de amanhã', 'depois de amanha'],
    next_week: ['na próxima semana', 'próxima semana', 'proxima semana'],
    this_weekend: ['este fim de semana'],
    next_weekend: [
      'no próximo fim de semana',
      'próximo fim de semana',
      'proximo fim de semana',
    ],
    next_year: ['no próximo ano', 'próximo ano', 'proximo ano'],
    next_month: ['no próximo mês', 'próximo mês', 'proximo mes'],
    end_of_month: ['fim do mês', 'fim do mes'],
  },
  [Locales.PtBR]: {
    today: ['hoje'],
    tomorrow: ['amanhã', 'amanha'],
    day_after_tomorrow: ['depois de amanhã', 'depois de amanha'],
    next_week: ['na próxima semana', 'próxima semana', 'proxima semana'],
    this_weekend: ['este fim de semana'],
    next_weekend: [
      'no próximo fim de semana',
      'próximo fim de semana',
      'proximo fim de semana',
    ],
    next_year: ['no próximo ano', 'próximo ano', 'proximo ano'],
    next_month: ['no próximo mês', 'próximo mês', 'proximo mes'],
    end_of_month: ['fim do mês', 'fim do mes'],
  },
};

// The emitter writes one of these words back into the input, so it must pick a form the matcher
// above accepts; both sides therefore read this one table.
export const getTodayKeyword = (language: Locales) =>
  RELATIVE_DATE_KEYWORDS[language].today[0];

export const getTomorrowKeyword = (language: Locales) =>
  RELATIVE_DATE_KEYWORDS[language].tomorrow[0];

export const getDisplayDate = (
  keyword: string,
  language: Locales,
  dateFormat: DateFormatOptions,
  isDeadline = false,
) => {
  if (!keyword || !keyword.trim()) {
    return null;
  }
  const now = new Date();
  // Use start of day for comparisons to avoid time-related issues.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let date = new Date();

  const currentDayIndexMap = getDayIndexMap(language);
  const currentMonthsMap = getMonthMap(language);
  const localeOrdinals = getRruleKeywords(language).ordinals;
  const dayNumberArticle = DAY_NUMBER_ARTICLES[language];
  const lowerKeyword = keyword.toLowerCase().trim();

  if (CLEAR_DATE_KEYWORDS[language].includes(lowerKeyword)) {
    return isDeadline ? null : { date: null, dateIsoString: null };
  }

  const parts = lowerKeyword.split(/\s+/);
  const todayDay = now.getDay();

  const k = RELATIVE_DATE_KEYWORDS[language];

  // --- Start of Corrected Logic ---
  // Specific keywords should take precedence over generic patterns like "next [day]"
  // to prevent misinterpretation (e.g. "next month" being parsed as "next monday").

  const nextDayForm = NEXT_DAY_FORMS[language].find((form) =>
    form.pattern.test(lowerKeyword),
  );

  if (k.today.includes(lowerKeyword)) {
    // Stays today
  } else if (k.tomorrow.includes(lowerKeyword)) {
    date.setDate(now.getDate() + 1);
  } else if (k.day_after_tomorrow.includes(lowerKeyword)) {
    date.setDate(now.getDate() + 2);
  } else if (k.next_week.includes(lowerKeyword)) {
    if (isDeadline) {
      const daysUntilNextFriday = ((5 - todayDay + 7) % 7) + 7;
      date.setDate(now.getDate() + daysUntilNextFriday);
    } else {
      date.setDate(now.getDate() - todayDay + 1 + 7); // Next Monday
    }
  } else if (k.this_weekend.includes(lowerKeyword)) {
    const diff = (6 + 7 - todayDay) % 7; // Get to Saturday
    date.setDate(now.getDate() + diff);
  } else if (k.next_weekend.includes(lowerKeyword)) {
    const diff = (6 + 7 - todayDay) % 7; // Get to this Saturday, then add 7 days
    date.setDate(now.getDate() + diff + 7);
  } else if (k.next_year.includes(lowerKeyword)) {
    date = new Date(now.getFullYear() + 1, 0, 1);
  } else if (k.next_month.includes(lowerKeyword)) {
    const originalDay = now.getDate();
    date.setMonth(now.getMonth() + 1);
    if (date.getDate() !== originalDay) {
      // Month overflow happened (e.g. Jan 31 -> March 3).
      // Set to 0 to get the last day of the previous month (which is our target month).
      date.setDate(0);
    }
  } else if (k.end_of_month.includes(lowerKeyword)) {
    date = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
  }

  // --- Generic Logic for "next [day]" patterns ---
  else if (nextDayForm) {
    const dayStr = lowerKeyword.match(nextDayForm.pattern)?.[1] ?? '';
    const targetDayKey = Object.keys(currentDayIndexMap).find((d) =>
      dayStr.startsWith(d),
    );
    if (!targetDayKey) {
      return null;
    }
    const diff = (currentDayIndexMap[targetDayKey] + 7 - todayDay) % 7;
    date.setDate(
      now.getDate() +
        (nextDayForm.week === NextDayWeek.Following ? diff + 7 : diff || 7),
    );
  } else if (
    parts.length === 3 &&
    currentMonthsMap.hasOwnProperty(parts[0]) &&
    !isNaN(parseInt(parts[1])) &&
    /^\d{4}$/.test(parts[2])
  ) {
    // "feb 9 2026"
    const day = parseInt(parts[1], 10);
    const monthIndex = currentMonthsMap[parts[0]];
    const year = parseInt(parts[2], 10);
    if (monthIndex !== undefined) {
      const tempDate = new Date(year, monthIndex, day);
      if (
        tempDate.getFullYear() === year &&
        tempDate.getMonth() === monthIndex &&
        tempDate.getDate() === day
      ) {
        date = tempDate;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } else if (
    parts.length === 3 &&
    !isNaN(parseInt(parts[0])) &&
    currentMonthsMap.hasOwnProperty(parts[1]) &&
    /^\d{4}$/.test(parts[2])
  ) {
    // "9 feb 2026"
    const day = parseInt(parts[0], 10);
    const monthIndex = currentMonthsMap[parts[1]];
    const year = parseInt(parts[2], 10);
    if (monthIndex !== undefined) {
      const tempDate = new Date(year, monthIndex, day);
      if (
        tempDate.getFullYear() === year &&
        tempDate.getMonth() === monthIndex &&
        tempDate.getDate() === day
      ) {
        date = tempDate;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } else if (
    parts.length === 2 &&
    !isNaN(parseInt(parts[0])) &&
    currentMonthsMap.hasOwnProperty(parts[1])
  ) {
    // "28 jun"
    const day = parseInt(parts[0], 10);
    const monthIndex = currentMonthsMap[parts[1]];
    if (monthIndex !== undefined) {
      const tempDate = new Date(now.getFullYear(), monthIndex, day);
      if (tempDate.getMonth() !== monthIndex || tempDate.getDate() !== day) {
        return null;
      }
      if (tempDate < today) {
        tempDate.setFullYear(now.getFullYear() + 1);
      }
      date = tempDate;
    } else {
      return null;
    }
  } else if (
    parts.length === 2 &&
    currentMonthsMap.hasOwnProperty(parts[0]) &&
    !isNaN(parseInt(parts[1]))
  ) {
    // "jun 28"
    const day = parseInt(parts[1], 10);
    const monthIndex = currentMonthsMap[parts[0]];
    if (monthIndex !== undefined) {
      const tempDate = new Date(now.getFullYear(), monthIndex, day);
      if (tempDate.getMonth() !== monthIndex || tempDate.getDate() !== day) {
        return null;
      }
      if (tempDate < today) {
        tempDate.setFullYear(now.getFullYear() + 1);
      }
      date = tempDate;
    } else {
      return null;
    }
  } else if (
    new RegExp(`^\\d{1,2}${localeOrdinals}$`).test(lowerKeyword) ||
    (!!dayNumberArticle &&
      new RegExp(`^${dayNumberArticle}\\s\\d{1,2}$`).test(lowerKeyword))
  ) {
    const day = parseInt(lowerKeyword.replace(/^\D+\s*/, ''), 10);
    const tempDate = new Date(now.getFullYear(), now.getMonth(), day);
    if (tempDate.getMonth() !== now.getMonth() || tempDate.getDate() !== day) {
      return null;
    }
    if (tempDate < today) {
      tempDate.setMonth(now.getMonth() + 1);
    }
    date = tempDate;
  } else if (/^\d{1,2}[./]\d{1,2}(?:[./]\d{2,4})?$/.test(lowerKeyword)) {
    const separator = lowerKeyword.includes('/') ? '/' : '.';
    const dateParts = lowerKeyword.split(separator);

    let day, month;

    // Based on the dateFormat setting, interpret the first two parts as either day/month or month/day.
    if (dateFormat === DateFormatOptions.DayMonthYear) {
      day = parseInt(dateParts[0], 10);
      month = parseInt(dateParts[1], 10) - 1;
    } else {
      month = parseInt(dateParts[0], 10) - 1;
      day = parseInt(dateParts[1], 10);
    }
    let year =
      dateParts.length === 3
        ? dateParts[2].length === 2
          ? 2000 + parseInt(dateParts[2], 10)
          : parseInt(dateParts[2], 10)
        : now.getFullYear();
    if (new Date(year, month, day) < today && dateParts.length < 3) {
      year++;
    }
    const tempDate = new Date(year, month, day);
    if (
      tempDate.getFullYear() !== year ||
      tempDate.getMonth() !== month ||
      tempDate.getDate() !== day
    ) {
      return null;
    }
    date = tempDate;
  } else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(lowerKeyword)) {
    const tempDate = new Date(lowerKeyword);
    if (isNaN(tempDate.getTime())) {
      return null;
    }
    date = new Date(tempDate.getTime() + tempDate.getTimezoneOffset() * 60000);
  } else {
    const targetDayKey = Object.keys(currentDayIndexMap).find((d) =>
      lowerKeyword.startsWith(d),
    );
    if (targetDayKey) {
      const targetDay = currentDayIndexMap[targetDayKey];
      const diff = (targetDay + 7 - now.getDay()) % 7;
      date.setDate(now.getDate() + (diff === 0 ? 7 : diff));
    } else {
      return null;
    }
  }

  return {
    date,
    dateIsoString: date.toISOString(),
  };
};
